import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Activity, Loader2, Pencil, RotateCcw, AlertCircle, Circle,
} from 'lucide-react';
import { api } from '../../api/client';
import { unwrap, unwrapArray } from '../../lib/api-envelope';
import { fmtQty } from '../../lib/display-labels';
import StatTile from '../../components/ui/StatTile';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import {
  LOAD_FAILURE_QUERY_OPTIONS,
  queryFailureError,
  queryLoadFailure,
  type LoadFailureKind,
} from '../../lib/load-failure';
import {
  METRIC_DEFS, METRIC_WINDOW_LABELS, describeRate, readRate, rateSubtext,
  readExposureMonth, formatPeriodMonth, exposureTag, fmtExposureHours, fmtExposureCount,
  EXPOSURE_TAG_LABELS,
  type MetricWindow, type SafetyMetrics, type ExposureMonth, type ExposureTag, type MetricDef,
} from '../../lib/safety-metrics';

type Tab = 'metrics' | 'exposure';

/**
 * Safety Metrics — the six OH&S rates and the exposure hours behind them.
 *
 * ── WHY THIS SCREEN IS CAREFUL ───────────────────────────────────────────────
 * TRIF goes on prequalification packages. A customer's HSE department compares
 * it against a threshold before awarding work, so a rate that renders 0.00 when
 * the truth is "nobody entered any hours" is not a cosmetic bug — it is a
 * number a company will quote in a bid.
 *
 * Three states, kept distinct everywhere on this page:
 *   —  + "No clock-in or ticket hours …"  → no month in the window has hours
 *   —  + "Recorded as zero"               → covered months sum to zero
 *   0.00, full strength, with its figures beneath → a real, earned result
 *
 * The decision itself lives in `describeRate` so the metric tiles and any
 * future consumer cannot disagree about it. Nothing on this page computes a
 * rate — every value is displayed exactly as the API sent it.
 */
export default function SafetyMetricsScreen() {
  const [tab, setTab] = useState<Tab>('metrics');
  const [window, setWindow] = useState<MetricWindow>('rolling12');

  const metricsQuery = useQuery({
    queryKey: ['safety-metrics', window],
    queryFn: () => api.safety.metrics(window).then((r) => unwrap<SafetyMetrics>(r.data)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const { data: metrics, isLoading } = metricsQuery;
  /* `queryLoadFailure`, not `isError`: a retrying 5xx sits PAUSED with no
   * error, and `isError` alone read that as still loading forever. */
  const metricsFailure = queryLoadFailure(metricsQuery);

  return (
    <div className="space-y-4">
      {/* Under `SafetyLayout`: the band holds the <h1> and the section strip
        * names this page. What stays here is this page's own inner strip — the
        * second tab level, smaller, against the panel it re-titles — and the
        * window picker beside it. */}
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-fieldiq-border">
        <div className="flex items-center gap-5">
          {([
            { key: 'metrics', label: 'Metrics' },
            { key: 'exposure', label: 'Exposure Entry' },
          ] as { key: Tab; label: string }[]).map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              aria-pressed={tab === t.key}
              /* The amber rule and the weight carry the active state; the word
               * stays theme ink. Amber as ink is 4.36 on this surface in light. */
              className={`-mb-px border-b-2 py-2 text-xs transition-colors ${
                tab === t.key
                  ? 'border-fi-amber font-extrabold text-fieldiq-text'
                  : 'border-transparent font-semibold text-fieldiq-muted hover:text-fieldiq-text'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {/* Window toggle. Both options are always offered — a tenant with no
          * data still needs to see that the YTD window exists. */}
        <div className="mb-2 flex items-center overflow-hidden rounded-lg border border-fieldiq-border">
          {(Object.keys(METRIC_WINDOW_LABELS) as MetricWindow[]).map((w) => (
            <button
              key={w}
              onClick={() => setWindow(w)}
              aria-pressed={window === w}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                window === w
                  ? 'bg-fieldiq-accent text-fieldiq-dark'
                  : 'bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
              }`}
            >
              {METRIC_WINDOW_LABELS[w]}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        {tab === 'metrics' ? (
          <MetricsTab
            metrics={metrics ?? null}
            isLoading={isLoading}
            failure={metricsFailure}
            error={queryFailureError(metricsQuery)}
            window={window}
            onGoToExposure={() => setTab('exposure')}
          />
        ) : (
          <ExposureTab
            /* The window the metrics used, so both tabs show the same months.
             * Until metrics load, the API defaults to the same rolling-12. */
            from={metrics?.periodStart?.slice(0, 7)}
            to={metrics?.periodEnd?.slice(0, 7)}
          />
        )}
      </div>
    </div>
  );
}

// ─── Metrics tab ─────────────────────────────────────────────────────────────

function MetricsTab({
  metrics, isLoading, failure, error, window, onGoToExposure,
}: {
  metrics: SafetyMetrics | null;
  isLoading: boolean;
  failure: LoadFailureKind | null;
  error: unknown;
  window: MetricWindow;
  onGoToExposure: () => void;
}) {
  if (isLoading && !failure) {
    return (
      <div className="flex items-center gap-2 text-fieldiq-muted py-12 justify-center">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading metrics…
      </div>
    );
  }

  /* A failed load is NOT an empty state. Six em dashes reading "no exposure
   * data" would be a confident, wrong answer about the company's safety record
   * when the truth is the request did not come back. */
  /* The refused/unreachable split is kept: "ask an admin" and "try again" are
   * different remedies. A 200 this screen cannot read has no error to classify
   * and is reported as unreachable. */
  if (failure || !metrics) {
    return (
      <LoadFailureNotice
        error={error}
        kind={failure ?? 'unreachable'}
        subject="safety metrics"
      />
    );
  }

  const monthsCovered = metrics.exposure?.monthsCovered ?? null;
  const unclassified = metrics.counts?.unclassified ?? 0;

  return (
    <>
      <div data-testid="metrics-window-summary" className="text-sm text-fieldiq-muted">
        {METRIC_WINDOW_LABELS[window]} · {formatPeriodMonth(metrics.periodStart)} – {formatPeriodMonth(metrics.periodEnd)}
        {/* Any source counts: clock-in, tickets or an office edit. */}
        {typeof monthsCovered === 'number' && (
          <> · {monthsCovered} of {metrics.exposure?.monthsInWindow ?? '—'} months have hours</>
        )}
        {/* The API's window total, the figure every rate divides by. Read, never
          * summed from the months; null (no month covered) is an em dash. */}
        {' · '}{fmtQty(metrics.exposure?.hoursWorked, 0)} hrs worked
      </div>

      {/* An unreviewed backlog understates every recordable rate on this page.
        * Shown rather than buried — the alternative is a flattering TRIF whose
        * numerator is missing incidents nobody has classified yet. */}
      {unclassified > 0 && (
        <div className="bg-fi-amber/10 border border-fi-amber/40 rounded-xl px-4 py-3 flex items-start gap-3">
          <AlertCircle className="w-4 h-4 text-fieldiq-accent-signal shrink-0 mt-0.5" aria-hidden="true" />
          {/* Theme ink on the wash; the wash and the icon carry the amber. */}
          <div className="text-sm text-fieldiq-text">
            <span className="font-medium">
              {unclassified} incident{unclassified === 1 ? '' : 's'} not yet classified.
            </span>{' '}
            Until each is given a recordable classification it counts toward nothing, so the
            rates below are understated.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {METRIC_DEFS.map((def) => (
          <MetricTile
            key={def.key}
            def={def}
            raw={metrics.rates?.[def.key]}
            monthsCovered={monthsCovered}
            onGoToExposure={onGoToExposure}
          />
        ))}
      </div>

      <p className="text-xs text-fieldiq-muted">
        TRIF, LTIF, DART, Severity and
        Near-Miss Frequency use a base of {metrics.settings?.trifBase?.toLocaleString('en-CA') ?? '—'} hours;
        MVIR uses {metrics.settings?.mvirBase?.toLocaleString('en-CA') ?? '—'} km.
        Change these in Settings → Safety → Metrics.
      </p>
    </>
  );
}

function MetricTile({
  def, raw, monthsCovered, onGoToExposure,
}: {
  def: MetricDef;
  raw: any;
  monthsCovered: number | null;
  onGoToExposure: () => void;
}) {
  const rate = readRate(raw);
  const display = describeRate(rate, def);
  const missing = display.kind === 'missing';

  const icon = def.key === 'nearMissFrequency'
    ? <Circle size={20} className={missing ? 'text-fieldiq-muted' : 'text-foreground'} />
    : <Activity size={20} className={missing ? 'text-fieldiq-muted' : 'text-foreground'} />;

  return (
    <StatTile
      /* A missing rate is drawn muted; a real one — INCLUDING 0.00 — is drawn
       * at full strength in the metric's own tone. That contrast is the whole
       * point of the screen, so it is expressed as tone, not as a footnote. */
      tone={missing ? 'neutral' : def.tone}
      icon={icon}
      value={
        <span className={missing ? 'text-fieldiq-muted' : undefined}>{display.text}</span>
      }
      label={<><span className="font-medium text-foreground">{def.label}</span><br />{def.description}</>}
      subtext={
        missing ? (
          <>
            <span className="block">{display.reason}</span>
            <button
              onClick={onGoToExposure}
              className="text-fieldiq-accent hover:underline mt-1"
            >
              Enter exposure →
            </button>
          </>
        ) : (
          rateSubtext(rate, def, monthsCovered)
        )
      }
    />
  );
}

// ─── Exposure tab ────────────────────────────────────────────────────────────

/**
 * Badge fill per tag. Two meanings, two hues: blue = the API worked it out,
 * amber = the office typed it. Clock-in, tickets and both share blue on purpose
 * and the label tells them apart — a hue per basis would add colours that mean
 * something on this page only.
 */
const TAG_FILL: Record<ExposureTag, string> = {
  clock_in: 'bg-fi-signal-blue',
  tickets: 'bg-fi-signal-blue',
  tickets_and_clock_in: 'bg-fi-signal-blue',
  edited: 'bg-fi-signal-amber',
};

function ExposureTagBadge({ month }: { month: ExposureMonth }) {
  const tag = exposureTag(month);
  return (
    <span
      className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2 py-0.5 text-[0.65rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${
        tag ? TAG_FILL[tag] : 'bg-fi-signal-slate'
      }`}
    >
      {tag ? EXPOSURE_TAG_LABELS[tag] : '—'}
    </span>
  );
}

/** The hours figure, plus the one line of context under it. */
function HoursCell({ month }: { month: ExposureMonth }) {
  return (
    <div className="space-y-0.5">
      <div className={`tabular-nums ${month.effectiveHours === null ? 'text-fieldiq-muted' : 'font-semibold text-foreground'}`}>
        {fmtExposureHours(month.effectiveHours)}
      </div>
      {month.effectiveHours === null && (
        <div className="text-xs text-fieldiq-muted">No approved clock-in or tickets</div>
      )}
      {/* What the edit replaced, so the office can see the gap it opened. */}
      {month.source === 'edited' && (
        <div className="text-xs text-fieldiq-muted tabular-nums">
          Auto {fmtExposureHours(month.autoHours)}
        </div>
      )}
    </div>
  );
}

/** Held as strings so an empty box means "leave it alone", never zero. */
type Draft = { hoursWorked: string; kmDriven: string; employeeCount: string };

/**
 * The hours box starts EMPTY on an auto month, with the auto figure as its
 * placeholder. Prefilling it would send the auto figure back on a km-only save
 * and freeze the month as an override nobody asked for.
 */
const toDraft = (m: ExposureMonth): Draft => ({
  hoursWorked: m.source === 'edited' && m.overrideHours !== null ? String(m.overrideHours) : '',
  kmDriven: m.kmDriven === null ? '' : String(m.kmDriven),
  employeeCount: m.employeeCount === null ? '' : String(m.employeeCount),
});

/**
 * Only the fields that changed. An unchanged box is omitted.
 *
 * AN EMPTIED BOX MEANS DIFFERENT THINGS PER FIELD. km: emptying a recorded
 * figure sends `kmDriven: null`, which the PUT clears to NULL — the office's
 * way back to "no km recorded", shown as an em dash (a typed 0 is sent as 0 and
 * stays 0). Hours and headcount: an emptied box is still omitted — `hoursWorked:
 * null` clears the OVERRIDE (that is the Clear button's job), and the PUT has no
 * clear for employeeCount.
 */
function changedFields(initial: Draft, draft: Draft) {
  const body: { hoursWorked?: number; kmDriven?: number | null; employeeCount?: number } = {};
  (Object.keys(draft) as (keyof Draft)[]).forEach((k) => {
    const v = draft[k].trim();
    if (v === initial[k]) return;
    // Reached only when the box moved, so an emptied km here held a figure.
    if (v === '') {
      if (k === 'kmDriven') body.kmDriven = null;
      return;
    }
    body[k] = Number(v);
  });
  return body;
}

const INPUT = 'w-full bg-fieldiq-dark border border-fieldiq-border rounded px-2 py-1 text-sm text-foreground placeholder:text-fieldiq-muted';

function ExposureTab({ from, to }: { from?: string; to?: string }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<{ month: string; initial: Draft; draft: Draft } | null>(null);
  const [error, setError] = useState<string | null>(null);

  /* A failed read is not "no hours" — that sentence tells an admin to go and
   * type figures the API may already hold. */
  const monthsQuery = useQuery({
    queryKey: ['safety-exposure-months', from ?? null, to ?? null],
    queryFn: () =>
      api.safety.exposureMonths(from && to ? { from, to } : undefined)
        .then((r) => unwrapArray<any>(r.data).map(readExposureMonth)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const months = monthsQuery.data ?? [];
  const monthsFailure = monthsQuery.data === undefined ? queryLoadFailure(monthsQuery) : null;
  const isLoading = monthsQuery.isLoading && !monthsFailure;

  /* Exposure IS the denominator — a saved month that leaves TRIF stale until a
   * refresh is indistinguishable from the save having failed. */
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['safety-exposure-months'] });
    queryClient.invalidateQueries({ queryKey: ['safety-metrics'] });
  };

  const saveMutation = useMutation({
    mutationFn: ({ month, body }: { month: string; body: ReturnType<typeof changedFields> }) =>
      api.safety.saveExposureMonth(month, body),
    onSuccess: () => { setEditing(null); setError(null); invalidate(); },
    onError: (e: any) => setError(e?.response?.data?.error ?? 'Could not save that month.'),
  });

  const clearMutation = useMutation({
    mutationFn: (month: string) => api.safety.clearExposureOverride(month),
    onSuccess: () => { setEditing(null); setError(null); invalidate(); },
    onError: (e: any) => setError(e?.response?.data?.error ?? 'Could not clear that edit.'),
  });

  const monthKey = (m: ExposureMonth) => m.periodStart.slice(0, 7);
  const startEdit = (m: ExposureMonth) => {
    const initial = toDraft(m);
    setEditing({ month: monthKey(m), initial, draft: initial });
    setError(null);
  };
  const setField = (field: keyof Draft, value: string) =>
    setEditing((e) => (e ? { ...e, draft: { ...e.draft, [field]: value } } : e));

  const renderActions = (m: ExposureMonth) => {
    const label = formatPeriodMonth(m.periodStart);
    if (editing?.month === monthKey(m)) {
      const body = changedFields(editing.initial, editing.draft);
      return (
        <div className="flex items-center gap-2">
          <button
            disabled={Object.keys(body).length === 0 || saveMutation.isPending}
            onClick={() => saveMutation.mutate({ month: editing.month, body })}
            className="fi-action fi-action--standard fi-action--primary disabled:opacity-50"
          >
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </button>
          <button
            onClick={() => setEditing(null)}
            className="rounded-lg border border-fieldiq-border bg-fieldiq-card px-3 py-1.5 text-xs font-medium text-foreground"
          >
            Cancel
          </button>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={() => startEdit(m)}
          aria-label={`Edit ${label}`}
          className="flex items-center gap-1 rounded-lg border border-fieldiq-border bg-fieldiq-card px-3 py-1.5 text-xs font-medium text-foreground"
        >
          <Pencil size={13} aria-hidden="true" /> Edit
        </button>
        {m.source === 'edited' && (
          <button
            disabled={clearMutation.isPending}
            onClick={() => clearMutation.mutate(monthKey(m))}
            aria-label={`Clear the edit on ${label}`}
            className="flex items-center gap-1 rounded-lg border border-fieldiq-border bg-fieldiq-card px-3 py-1.5 text-xs font-medium text-foreground disabled:opacity-50"
          >
            <RotateCcw size={13} aria-hidden="true" /> Clear
          </button>
        )}
      </div>
    );
  };

  const isEditing = (m: ExposureMonth) => editing?.month === monthKey(m);
  const hoursInput = (m: ExposureMonth) => (
    <input
      type="number" min="0" step="any" inputMode="decimal"
      aria-label={`Hours worked, ${formatPeriodMonth(m.periodStart)}`}
      value={editing?.draft.hoursWorked ?? ''}
      placeholder={m.autoHours === null ? '—' : fmtExposureHours(m.autoHours)}
      onChange={(e) => setField('hoursWorked', e.target.value)}
      className={INPUT}
    />
  );
  const kmInput = (m: ExposureMonth) => (
    <input
      type="number" min="0" step="any" inputMode="decimal"
      aria-label={`Distance in km, ${formatPeriodMonth(m.periodStart)}`}
      value={editing?.draft.kmDriven ?? ''}
      placeholder="—"
      onChange={(e) => setField('kmDriven', e.target.value)}
      className={INPUT}
    />
  );
  const countInput = (m: ExposureMonth) => (
    <input
      type="number" min="0" step="1" inputMode="numeric"
      aria-label={`Employee count, ${formatPeriodMonth(m.periodStart)}`}
      value={editing?.draft.employeeCount ?? ''}
      placeholder="—"
      onChange={(e) => setField('employeeCount', e.target.value)}
      className={INPUT}
    />
  );

  const emptyOrFailure = monthsFailure ? (
    <LoadFailureNotice error={queryFailureError(monthsQuery)} kind={monthsFailure} subject="exposure months" />
  ) : isLoading ? (
    <div className="py-8 text-center text-fieldiq-muted">Loading…</div>
  ) : months.length === 0 ? (
    <div className="py-8 text-center text-fieldiq-muted">No months in this window.</div>
  ) : null;

  return (
    <>
      <div>
        <h2 className="text-lg font-semibold text-foreground">Monthly exposure</h2>
        <p className="text-sm text-fieldiq-muted mt-1">
          Hours come from approved clock-in and tickets; edit a month to override it.
        </p>
      </div>

      {error && (
        <div role="alert" className="bg-fieldiq-danger/10 border border-fieldiq-danger/30 rounded-lg px-4 py-3 text-sm text-fieldiq-text">
          {error}
        </div>
      )}

      <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card overflow-hidden">
        {emptyOrFailure ? (
          <div className="px-4 py-2">{emptyOrFailure}</div>
        ) : (
          <>
            {/* md and up: the register. */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm min-w-[760px]" data-testid="exposure-months-table">
                <thead>
                  <tr className="border-b border-fieldiq-border text-left text-xs text-fieldiq-muted">
                    <th className="px-4 py-3 font-medium">Month</th>
                    <th className="px-4 py-3 font-medium">Hours worked</th>
                    <th className="px-4 py-3 font-medium">Source</th>
                    <th className="px-4 py-3 font-medium">Distance (km)</th>
                    <th className="px-4 py-3 font-medium">Employee count</th>
                    <th className="px-4 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {months.map((m) => (
                    <tr key={m.periodStart} data-testid={`exposure-month-${monthKey(m)}`} className="border-b border-fieldiq-border/50 last:border-0 align-top">
                      <td className="px-4 py-3 text-foreground whitespace-nowrap">{formatPeriodMonth(m.periodStart)}</td>
                      <td className="px-4 py-3 w-40">{isEditing(m) ? hoursInput(m) : <HoursCell month={m} />}</td>
                      <td className="px-4 py-3"><ExposureTagBadge month={m} /></td>
                      <td className="px-4 py-3 w-36 tabular-nums text-foreground">{isEditing(m) ? kmInput(m) : fmtExposureCount(m.kmDriven)}</td>
                      <td className="px-4 py-3 w-32 tabular-nums text-foreground">{isEditing(m) ? countInput(m) : fmtExposureCount(m.employeeCount)}</td>
                      <td className="px-4 py-3">{renderActions(m)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Below md: the same months as stacked cards. */}
            <ul className="md:hidden divide-y divide-fieldiq-border/50">
              {months.map((m) => (
                <li key={m.periodStart} className="px-4 py-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-foreground">{formatPeriodMonth(m.periodStart)}</span>
                    <ExposureTagBadge month={m} />
                  </div>
                  {isEditing(m) ? (
                    <div className="grid grid-cols-3 gap-2 text-xs text-fieldiq-muted">
                      <label>Hours{hoursInput(m)}</label>
                      <label>km{kmInput(m)}</label>
                      <label>Employees{countInput(m)}</label>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-2 text-xs text-fieldiq-muted">
                      <div>Hours<HoursCell month={m} /></div>
                      <div>km<div className="tabular-nums text-sm text-foreground">{fmtExposureCount(m.kmDriven)}</div></div>
                      <div>Employees<div className="tabular-nums text-sm text-foreground">{fmtExposureCount(m.employeeCount)}</div></div>
                    </div>
                  )}
                  {renderActions(m)}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </>
  );
}
